import React, { useEffect, useState } from "react";
import styles from "./Migration.module.css";

const DEST = "https://crossover.games";

export function migrationUrl(medium) {
  return `${DEST}?utm_source=nbaconnectgrid&utm_medium=${medium}`;
}

// 1. Dismissible site-wide banner. Dismissal persists for the session day.
export function MigrationBanner() {
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("migration_banner_dismissed") === "1"
  );
  if (dismissed) return null;
  return (
    <div className={styles.banner}>
      <a
        className={styles.bannerLink}
        href={migrationUrl("banner")}
        target="_blank"
        rel="noopener noreferrer"
      >
        🎉 We've moved! Play NBA Connect Grid and 20+ new daily games at{" "}
        <strong>crossover.games</strong> →
      </a>
      <button
        className={styles.bannerClose}
        aria-label="Dismiss"
        onClick={() => {
          sessionStorage.setItem("migration_banner_dismissed", "1");
          setDismissed(true);
        }}
      >
        ✕
      </button>
    </div>
  );
}

// 2. Full modal CTA, shown after a daily game is completed. Pass `medium`
// to attribute which game drove the click. Shows once per day per game.
export function MigrationModal({
  gameKey,
  medium = "post_game_modal",
  delayMs = 6000, // let the player see their result first
}) {
  const storageKey = `migration_modal_${gameKey}_${new Date().toDateString()}`;
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (localStorage.getItem(storageKey)) return;
    const t = setTimeout(() => setOpen(true), delayMs);
    return () => clearTimeout(t);
  }, [storageKey, delayMs]);
  if (!open) return null;
  const close = () => {
    localStorage.setItem(storageKey, "1");
    setOpen(false);
  };
  return (
    <div className={styles.modalOverlay} onClick={close}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalEmoji}>🏀🎬🏈</div>
        <h2 className={styles.modalTitle}>Keep playing on crossover.games</h2>
        <p className={styles.modalBody}>
          All your favorite daily games — plus unlimited modes, streaks, and
          20+ new games — are live at crossover.games.
        </p>
        <a
          className={styles.modalCta}
          href={migrationUrl(medium)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Keep playing →
        </a>
        <button className={styles.modalDismiss} onClick={close}>
          Not now
        </button>
      </div>
    </div>
  );
}

// 3. Timed redirect for informational (non-game) pages only.
// Never used on game routes — an in-progress grid must not be interrupted.
export function InfoRedirect({ children, seconds = 12, medium = "info_redirect" }) {
  const [left, setLeft] = useState(seconds);
  const [cancelled, setCancelled] = useState(false);
  useEffect(() => {
    if (cancelled) return;
    if (left <= 0) {
      window.location.href = migrationUrl(medium);
      return;
    }
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [left, cancelled, medium]);
  return (
    <>
      {!cancelled && (
        <div className={styles.redirectBar}>
          Taking you to <strong>crossover.games</strong> in {left}s…{" "}
          <button
            className={styles.redirectCancel}
            onClick={() => setCancelled(true)}
          >
            Stay here
          </button>
        </div>
      )}
      {children}
    </>
  );
}
